import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import RecipeCard from "../components/RecipeCard";
import { ArrowUpRight, Fish, Drumstick, Sprout, Camera, Leaf, BookMarked } from "lucide-react";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG =
  "https://images.unsplash.com/photo-1616671285410-2a676a9a433d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtaWNoZWxpbiUyMHN0YXIlMjBwbGF0aW5nfGVufDB8fHx8MTc4NTA5NzI0MHww&ixlib=rb-4.1.0&q=85";

const marqueeWords = [
  "Langoustines", "Grouse", "Bramble", "Kelp", "Fire", "Peat",
  "Mackerel", "Venison", "Turnip", "Sea buckthorn", "Whisky butter", "Foraged",
  "Cod save the queen", "Lettuce pray", "Olive the above", "Wok this way",
  "In for a penne", "Peas be with you", "Bear grill", "Nom de plume",
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [fowl, setFowl] = useState([]);
  const [larder, setLarder] = useState([]);
  const [weeklyPhoto, setWeeklyPhoto] = useState(null);
  const [editorsPick, setEditorsPick] = useState(null);

  useEffect(() => {
    // Poultry corner — pull a wider net then filter by title so we don't grab
    // recipes that merely mention "chicken stock" in the method.
    const POULTRY_RX = /\b(chicken|duck|goose|guinea fowl|quail|pigeon|pheasant|partridge|grouse|turkey|hen)\b/i;
    const NOT_POULTRY_RX = /\bliver\b/i; // liver dishes go in offal, not poultry
    Promise.all([
      api.get("/recipes?q=chicken&limit=10"),
      api.get("/recipes?q=duck&limit=6"),
      api.get("/recipes?q=guinea&limit=4"),
      api.get("/recipes?q=quail&limit=4"),
      api.get("/recipes?q=goose&limit=4"),
      api.get("/recipes?is_vegan=true&course=main&limit=6"),
    ])
      .then((results) => {
        const seen = new Set();
        const merged = [];
        for (const r of results.slice(0, 5)) {
          for (const rec of r.data) {
            if (seen.has(rec.id)) continue;
            const title = rec.title || "";
            if (!POULTRY_RX.test(title)) continue;      // must have a bird in the title
            if (NOT_POULTRY_RX.test(title)) continue;   // no chicken-liver dishes here
            seen.add(rec.id);
            merged.push(rec);
          }
        }
        const fowlIds = new Set(merged.slice(0, 4).map((r) => r.id));
        setFowl(merged.slice(0, 4));

        // Featured — pull recent, exclude fowl, and guarantee at least one vegan
        const veganPool = results[5].data.filter((r) => !fowlIds.has(r.id));
        api.get("/recipes?limit=12")
          .then((res) => {
            const nonFowl = res.data.filter((r) => !fowlIds.has(r.id));
            const hasVegan = nonFowl.slice(0, 6).some((r) => r.is_vegan);
            let final = nonFowl.slice(0, 6);
            if (!hasVegan && veganPool.length > 0) {
              // Swap in the freshest vegan we haven't shown
              final = [veganPool[0], ...nonFowl.filter((r) => r.id !== veganPool[0].id)].slice(0, 6);
            }
            setFeatured(final);
          })
          .catch(() => {});
      })
      .catch(() => {
        api.get("/recipes?limit=6").then((res) => setFeatured(res.data)).catch(() => {});
      });

    // Larder strip — prefer ingredients that already have hero images
    api.get("/ingredients")
      .then((res) => {
        const all = res.data || [];
        const ready = all.filter((i) => i.image_status === "ready");
        const rest = all.filter((i) => i.image_status !== "ready");
        setLarder([...ready, ...rest].slice(0, 6));
      })
      .catch(() => setLarder([]));

    // Photo of the week
    api.get("/cook_photos/photo_of_the_week")
      .then((res) => {
        if (res.data?.photo) setWeeklyPhoto(res.data);
      })
      .catch(() => setWeeklyPhoto(null));

    // Editor's pick
    api.get("/editors_pick")
      .then((res) => {
        if (res.data?.recipe) setEditorsPick(res.data);
      })
      .catch(() => setEditorsPick(null));
  }, []);

  return (
    <div className="min-h-screen bg-stone-950 pt-16">
      {/* HERO */}
      <section className="border-b border-stone-800">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 pt-6 sm:pt-10 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7 fade-up">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-px bg-orange-600" />
                <span className="eyebrow">Vol. 01 &middot; The Wandering Larder</span>
              </div>
              <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter text-stone-100">
                Good dinner,<br />
                <span className="italic text-orange-600">borrowed</span> from<br />
                <span className="text-stone-400">the whole</span><br />
                wide world.
              </h1>
              <p className="mt-10 max-w-xl text-lg text-stone-300 leading-relaxed">
                A Scottish kitchen with an itchy passport &mdash; part pot to plate,
                part plot to pate. Big on seafood, wild game, and honest veg with a
                proper vegan main on the side. Tell us where you are; we&apos;ll tell
                you what to shake and bake tonight <em className="text-orange-500 not-italic">(or bake and shake, we&apos;re easy).</em>
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link
                  to="/generate"
                  data-testid="hero-generate-btn"
                  className="group inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-8 py-4 uppercase tracking-[0.24em] text-xs font-medium transition-colors"
                >
                  Cook something tonight
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </Link>
                <Link
                  to="/library"
                  data-testid="hero-library-btn"
                  className="inline-flex items-center gap-2 text-stone-300 hover:text-orange-500 uppercase tracking-[0.24em] text-xs font-medium ink-link"
                >
                  Rummage the library
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <img
                  src={HERO_IMG}
                  alt="A plate worth stopping the car for"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col bg-stone-950 border border-stone-800 p-5 w-60">
                  <span className="eyebrow mb-2">Tonight, apparently</span>
                  <span className="font-serif-display italic text-2xl text-stone-100">
                    Charred mackerel, whisky-butter kelp
                  </span>
                </div>
                <div className="absolute -top-3 sm:-top-4 -right-2 sm:-right-4 magazine-number text-5xl sm:text-6xl select-none">
                  01
                </div>
                {/* Playful rotated sticker — hidden on narrow mobile */}
                <div className="hidden sm:block absolute bottom-16 -right-4 bg-lime-500 text-stone-950 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-mono-accent rotate-6"
                >
                  Peas, love &amp; understanding
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-b border-stone-800 py-6 overflow-hidden bg-stone-900/40">
        <div className="flex marquee-track whitespace-nowrap">
          {[...marqueeWords, ...marqueeWords].map((w, i) => {
            const color = i % 4 === 0 ? "text-orange-500" : i % 4 === 1 ? "text-yellow-400" : i % 4 === 2 ? "text-lime-400" : "text-stone-400";
            return (
              <span
                key={i}
                className={`font-serif-display italic text-3xl md:text-4xl ${color} px-8 flex items-center gap-8`}
              >
                {w}
                <span className="w-2 h-2 bg-orange-600 rounded-full" />
              </span>
            );
          })}
        </div>
      </section>

      {/* Three-pillar editorial */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32">
        <div className="grid md:grid-cols-3 gap-12 md:gap-6">
          {[
            {
              n: "01",
              icon: Fish,
              title: "Great with the fishy stuff",
              body: "Mackerel from the pier, langoustines from the creel, seaweed you probably tripped over. If it swims, wriggles or clings to a rock, we have a plaice for it. Sorry.",
            },
            {
              n: "02",
              icon: Drumstick,
              title: "Handy with game, too",
              body: "Grouse, venison, hare, pigeon — proper wild meat, cooked with respect (and a decent glug of something red). Fowl play encouraged. Bad puns compulsory.",
            },
            {
              n: "03",
              icon: Sprout,
              title: "And vegetables, obviously",
              body: "Vegetarian without the smug. Every recipe carries a genuine vegan main that actually tastes of something. No sad grains in bowls; no beige in the brigade.",
            },
          ].map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.n} className="border-t border-stone-800 pt-8 md:pr-10">
                <div className="flex items-baseline justify-between mb-8">
                  <span className="magazine-number text-4xl">{p.n}</span>
                  <Icon className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif-display text-3xl text-stone-100 leading-tight mb-4">
                  {p.title}
                </h3>
                <p className="text-stone-400 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured recipes */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 pb-16 sm:pb-24">
          <div className="flex items-end justify-between mb-12 border-b border-stone-800 pb-8">
            <div>
              <div className="eyebrow mb-3">Off the Pass</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-stone-100 tracking-tight">
                Recently <span className="italic text-orange-600">cooked</span>
              </h2>
            </div>
            <Link
              to="/library"
              data-testid="home-view-all"
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link"
            >
              The whole lot
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.slice(0, 6).map((r, i) => (
              <RecipeCard key={r.id} recipe={r} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Something Fowl This Way Comes */}
      {fowl.length > 0 && (
        <section className="bg-gradient-to-b from-orange-950/20 via-stone-950 to-stone-950 border-t border-b border-orange-900/40 py-16 sm:py-24">
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Drumstick className="w-5 h-5 text-yellow-500" strokeWidth={1.5} />
                  <span className="eyebrow text-yellow-500">Poultry corner</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-stone-100 tracking-tight leading-[0.95]">
                  Something <span className="italic text-orange-600">fowl</span>
                  <br />
                  this way comes.
                </h2>
                <p className="mt-6 text-stone-300 max-w-lg leading-relaxed">
                  Chicken, duck, goose &mdash; the birds get the same respect as the
                  venison and the langoustines. No <em>wing it</em> nonsense; just
                  proper roast dinner without the sad Sunday face.
                </p>
              </div>
              <Link
                to="/library?q=chicken"
                data-testid="home-fowl-view-all"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link"
              >
                All the birds
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-fowl-grid">
              {fowl.slice(0, 4).map((r, i) => (
                <RecipeCard key={r.id} recipe={r} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* From The Larder — hero ingredient strip */}
      {larder.length > 0 && (
        <section className="border-t border-stone-800 bg-stone-950 py-16 sm:py-24" data-testid="home-larder">
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Leaf className="w-5 h-5 text-lime-500" strokeWidth={1.5} />
                  <span className="eyebrow text-lime-500">From the Larder</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-stone-100 tracking-tight leading-[0.95]">
                  Meet the <span className="italic text-orange-600">hero</span>
                  <br />
                  ingredients.
                </h2>
                <p className="mt-6 text-stone-300 max-w-lg leading-relaxed">
                  Grouse, sea buckthorn, offal, kelp &mdash; the stars every serious
                  cook keeps close. A deep-dive on each: when to pick it, how to buy it,
                  what to do with it before it goes off.
                </p>
              </div>
              <Link
                to="/ingredients"
                data-testid="home-larder-view-all"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link"
              >
                Full larder
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="home-larder-grid">
              {larder.map((ing) => (
                <Link
                  key={ing.id}
                  to={`/ingredients/${ing.slug}`}
                  data-testid={`larder-tile-${ing.slug}`}
                  className="group relative aspect-[3/4] bg-stone-900 border border-stone-800 hover:border-orange-800 overflow-hidden transition-colors"
                >
                  {ing.image_status === "ready" ? (
                    <img
                      src={`${BACKEND}/api/ingredients/${ing.slug}/og_image`}
                      alt={ing.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-stone-800 to-stone-950" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="text-[9px] uppercase tracking-[0.24em] text-lime-500">
                      {ing.category}
                    </span>
                    <h3 className="font-serif-display text-2xl text-stone-100 leading-tight mt-1">
                      {ing.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Editor's Pick — admin pinned recipe */}
      {editorsPick?.recipe && (
        <section
          className="border-t border-stone-800 bg-stone-950 py-16 sm:py-24"
          data-testid="home-editors-pick"
        >
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-6 lg:order-2">
                <div className="relative">
                  <div className="aspect-[5/4] w-full bg-stone-900 overflow-hidden">
                    {editorsPick.recipe.image_status === "ready" && (
                      <img
                        src={`${BACKEND}/api/recipes/${editorsPick.recipe.id}/og_image`}
                        alt={editorsPick.recipe.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="absolute -top-4 -right-4 bg-lime-500 text-stone-950 px-4 py-2 uppercase tracking-[0.28em] text-[10px] font-medium">
                    Editor&apos;s pick
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <BookMarked className="w-5 h-5 text-lime-500" strokeWidth={1.5} />
                  <span className="eyebrow text-lime-500">This week&apos;s showstopper</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-stone-100 tracking-tight leading-[0.95]">
                  <span className="italic text-orange-600">Cook</span> this
                  <br />
                  this week.
                </h2>
                <Link
                  to={`/recipe/${editorsPick.recipe.id}`}
                  data-testid="home-editors-pick-link"
                  className="mt-8 inline-flex items-baseline gap-2 group"
                >
                  <h3 className="font-serif-display text-3xl md:text-4xl text-stone-100 leading-tight tracking-tight group-hover:text-orange-500 transition-colors">
                    {editorsPick.recipe.title}
                  </h3>
                  <ArrowUpRight className="w-5 h-5 text-orange-500 shrink-0" />
                </Link>
                {editorsPick.pick?.note && (
                  <blockquote className="mt-6 font-serif-display italic text-xl md:text-2xl text-stone-300 leading-snug border-l-2 border-lime-800/60 pl-6">
                    &ldquo;{editorsPick.pick.note}&rdquo;
                  </blockquote>
                )}
                <p className="mt-6 text-xs uppercase tracking-[0.24em] text-stone-500">
                  Pinned by {editorsPick.pick?.pinned_by || "the kitchen"}
                  {editorsPick.recipe.region && (
                    <>
                      {" · "}
                      {editorsPick.recipe.region}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Photo of the Week — cook photo hero */}
      {weeklyPhoto?.photo && (
        <section
          className="border-t border-stone-800 bg-stone-900/30 py-16 sm:py-24"
          data-testid="home-photo-of-week"
        >
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-6">
                <div className="relative">
                  <div className="aspect-[4/5] w-full bg-stone-900 overflow-hidden">
                    <img
                      src={`${BACKEND}/api/cook_photos/${weeklyPhoto.photo.id}/image`}
                      alt={weeklyPhoto.photo.caption || "cook photo of the week"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -top-4 -left-4 bg-orange-600 text-stone-950 px-4 py-2 uppercase tracking-[0.28em] text-[10px] font-medium">
                    Photo of the week
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-6">
                  <Camera className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  <span className="eyebrow text-orange-500">Cooks&apos; gallery</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-stone-100 tracking-tight leading-[0.95]">
                  Someone <span className="italic text-orange-600">actually</span>
                  <br />
                  cooked this.
                </h2>
                {weeklyPhoto.photo.caption && (
                  <blockquote className="mt-8 font-serif-display italic text-xl md:text-2xl text-stone-200 leading-snug border-l-2 border-orange-800/60 pl-6">
                    &ldquo;{weeklyPhoto.photo.caption}&rdquo;
                  </blockquote>
                )}
                <p className="mt-6 text-xs uppercase tracking-[0.24em] text-stone-500">
                  by {weeklyPhoto.photo.user_display_name || "an anonymous cook"}
                  {weeklyPhoto.recipe?.title && (
                    <>
                      {" · from "}
                      <Link
                        to={`/recipe/${weeklyPhoto.recipe.id}`}
                        data-testid="home-photo-of-week-recipe"
                        className="text-orange-500 hover:text-orange-400 ink-link"
                      >
                        {weeklyPhoto.recipe.title}
                      </Link>
                    </>
                  )}
                </p>
                <p className="mt-10 max-w-lg text-sm text-stone-400 leading-relaxed">
                  Cooked something worth showing off? Add a photo to any recipe&apos;s
                  cook gallery. The best from each week lands here.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="border-t border-b border-stone-800 bg-stone-900/30 relative overflow-hidden">
        {/* Big rotated background number */}
        <div
          aria-hidden="true"
          className="absolute -right-16 -top-12 font-serif-display italic text-[24rem] text-orange-900/20 leading-none select-none pointer-events-none"
        >
          04
        </div>

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32 grid md:grid-cols-12 gap-10 lg:gap-16 items-center relative">
          {/* Poster block */}
          <div className="md:col-span-5">
            <div className="border-4 border-orange-600 p-8 md:p-10 bg-stone-950 relative">
              <div
                aria-hidden="true"
                className="absolute -top-4 -left-4 bg-yellow-500 text-stone-950 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-mono-accent -rotate-3"
              >
                Hello, cook
              </div>
              <div className="magazine-number text-6xl mb-4 leading-none">&amp;</div>
              <div className="font-serif-display text-3xl md:text-4xl leading-[0.9] tracking-tighter text-stone-100">
                The Kitchen
              </div>
              <div className="mt-4 text-xs uppercase tracking-[0.24em] text-orange-500">
                Anonymous &middot; apologetic wine drinker
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {["Seafood", "Game", "Vegetables", "Fire", "Fermenting", "World"].map((t, i) => (
                  <span
                    key={t}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${
                      i % 3 === 0
                        ? "bg-orange-700 text-stone-50 border-orange-700"
                        : i % 3 === 1
                        ? "bg-yellow-500 text-stone-950 border-yellow-500"
                        : "bg-stone-900 text-stone-200 border-stone-700"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-7 relative">
            <div className="eyebrow mb-6 text-yellow-500">A word from the kitchen</div>
            <blockquote className="font-serif-display text-xl md:text-3xl text-stone-100 leading-[1.15] tracking-tight">
              {`\u201C`}You want restaurant food? Go to a restaurant. You want{" "}
              <span className="italic text-orange-600">supper</span> &mdash; the sort that
              ends with pudding, a proper argument, and someone stealing the last roast tattie &mdash;
              you cook it yourself. Or, as my nan says: no meat without heat, no love without oven.{`\u201D`}
            </blockquote>
            <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.24em] text-stone-500">
              <span className="w-10 h-px bg-orange-600" />
              The kitchen, over a glass of red
            </div>
          </div>
        </div>
      </section>

      {/* Skills teaser section */}
      <section className="border-t border-b border-stone-800 bg-stone-900/40 py-16 sm:py-24">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-yellow-500" />
              <span className="eyebrow text-yellow-500">The Skills Room</span>
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-stone-100 leading-[0.9] tracking-tighter">
              Sharpen up your <span className="italic text-orange-600">knife hand</span>.
            </h2>
            <p className="mt-6 text-stone-300 leading-relaxed max-w-lg">
              Butchery, boning, knife-care, hygiene &mdash; short lessons on the fundamentals that
              separate a wobbly Wednesday from a proper Sunday roast.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Break down a chicken", "Bone a lamb leg", "Fillet a mackerel", "Sharpen a knife", "Clean as you go"].map((s, i) => (
                <span
                  key={s}
                  className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${
                    i % 3 === 0 ? "bg-orange-700 text-stone-50 border-orange-700"
                    : i % 3 === 1 ? "bg-yellow-500 text-stone-950 border-yellow-500"
                    : "bg-stone-900 text-stone-200 border-stone-700"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>
            <Link
              to="/skills"
              data-testid="home-skills-cta"
              className="mt-10 inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-8 py-4 uppercase tracking-[0.24em] text-xs font-medium transition-colors"
            >
              Open the skills room
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-3">
            {[
              { n: "01", label: "Butchery" },
              { n: "02", label: "Boning" },
              { n: "03", label: "Prep" },
              { n: "04", label: "Knife care" },
              { n: "05", label: "Hygiene" },
              { n: "+", label: "More soon" },
            ].map((s) => (
              <div
                key={s.n}
                className="border border-stone-800 bg-stone-950 p-6 aspect-[3/2] flex flex-col justify-between"
              >
                <div className="magazine-number text-3xl">{s.n}</div>
                <div className="font-serif-display italic text-xl text-stone-100">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 py-16 sm:py-24 text-center">
        <div className="eyebrow mb-6">Right then</div>
        <h2 className="font-serif-display text-3xl md:text-5xl text-stone-100 leading-none tracking-tighter">
          What&apos;s for <span className="italic text-orange-600">tea</span>?
        </h2>
        <p className="mt-6 text-stone-400 max-w-xl mx-auto text-sm italic">
          Or, as our head chef mutters after the third glass:
          &ldquo;a well-boiled icicle&rdquo; &mdash; possibly meaning a well-oiled bicycle.
          Either way, dinner shortly.
        </p>
        <Link
          to="/generate"
          data-testid="footer-cta"
          className="inline-flex mt-10 items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-10 py-4 uppercase tracking-[0.24em] text-xs font-medium transition-colors"
        >
          Let&apos;s find out
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-stone-800 py-10 text-center">
        <p className="font-serif-display italic text-stone-500 text-sm">
          Cinder &amp; Sorrel &middot; A Scottish kitchen with an itchy passport &middot; MMXXVI
        </p>
      </footer>
    </div>
  );
};

export default Home;
